<template>
  <div class="flex flex-col gap-[0.3rem]">
    <div v-if="label" class="flex flex-wrap items-baseline gap-x-2 gap-y-1 min-h-[1.25rem]">
      <label :for="id || undefined" class="block truncate max-w-full flex-1 min-w-0 m-0 font-medium text-fg">
        {{ label }}
      </label>
      <span v-if="required" class="field-required-badge">Påkrevd</span>
    </div>
    <div class="relative flex items-center">
      <span v-if="$slots.prefix" class="absolute left-2 text-fg-muted">
        <slot name="prefix" />
      </span>
      <input
        :id="id"
        :class="[
          'w-full appearance-none py-2 px-2 rounded border border-solid text-sm bg-surface text-fg outline-none transition-colors',
          'placeholder:text-fg-muted hover:border-accent focus:border-accent',
          'focus:shadow-[0_0_0_2px_rgb(var(--color-accent-default)/0.1)]',
          $slots.prefix ? 'pl-8' : '',
          $slots.suffix ? 'pr-8' : '',
          hasError ? 'border-danger' : 'border-border',
        ]"
        :placeholder="placeholder"
        :type="type"
        :readonly="readonly"
        :autofocus="autofocus"
        :value="modelValue === null || modelValue === undefined ? '' : modelValue"
        :step="type === 'number' ? step : undefined"
        :min="type === 'number' ? min : undefined"
        :max="type === 'number' ? max : undefined"
        @blur="$emit('blur')"
        @change="emitValue($event, 'change')"
        @input="emitValue($event, 'input')"
      >
      <font-awesome-icon
        v-if="clearable && modelValue"
        :icon="['fal', 'times']"
        class="cursor-pointer p-2 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto text-fg-muted hover:text-fg"
        @click="clearInput"
      />
      <span v-if="$slots.suffix" class="absolute right-2 text-fg-muted cursor-pointer">
        <slot name="suffix" />
      </span>
    </div>
    <div v-if="hasError" class="text-danger mt-[0.2rem]">
      {{ displayError }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    required: false,
    default: null,
  },
  id: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  required: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  /** Tom streng = ingen feil. Eneste kilde til feiltekst og rød ramme. */
  error: {
    type: String,
    default: '',
  },
  step: {
    type: [String, Number],
    default: '1',
  },
  min: {
    type: [String, Number],
    default: null,
  },
  max: {
    type: [String, Number],
    default: null,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change', 'blur']);

const displayError = computed(() => String(props.error ?? '').trim());

const hasError = computed(() => displayError.value.length > 0);

function emitValue (event, eventType) {
  let value = event.target.value;

  if (props.type === 'number') {
    if (value === '') {
      value = null;
    } else {
      value = Number(value);
      if (Number.isNaN(value)) {
        value = null;
      }
    }
  }

  if (eventType === 'change') {
    emit('change', value);
  } else {
    emit('update:modelValue', value);
  }
}

function clearInput () {
  emit('update:modelValue', null);
  emit('change', null);
}
</script>

